import React from 'react';
import { Form, InputGroup } from 'react-bootstrap';

interface CareMapprTextareaProps {
  label?: string;
  labelClassName?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  rows?: number;
  className?: string;
  inputClassName?: string;
  controlId?: string;
  required?: boolean;
  disabled?: boolean;
  autoResize?: boolean;
}

const CareMapprTextarea = ({
  label,
  labelClassName = '',
  value,
  onChange,
  placeholder = '',
  rows = 1,
  className = '',
  controlId = 'textarea-field',
  required = false,
  disabled = false,
  inputClassName = '',
  autoResize = false,
  ...props
}: CareMapprTextareaProps) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const lastAppliedHeightRef = React.useRef<number | null>(null);
  const lastObservedWidthRef = React.useRef<number | null>(null);

  const adjustHeight = React.useCallback(
    (target?: HTMLTextAreaElement | null) => {
      if (!autoResize) return;
      const el = target || textareaRef.current;
      if (!el) return;
      // Prevent manual resize & scrollbar while auto-resizing
      el.style.setProperty('resize', 'none');
      el.style.setProperty('overflow-y', 'hidden');

      // Compute min height based on rows and computed styles, so rows acts as baseline
      const computed = window.getComputedStyle(el);
      const fontSize = parseFloat(computed.fontSize) || 16;
      const lineHeightRaw = parseFloat(computed.lineHeight);
      const lineHeight = isNaN(lineHeightRaw) ? 1.2 * fontSize : lineHeightRaw;
      const paddingTop = parseFloat(computed.paddingTop) || 0;
      const paddingBottom = parseFloat(computed.paddingBottom) || 0;
      const borderTop = parseFloat(computed.borderTopWidth) || 0;
      const borderBottom = parseFloat(computed.borderBottomWidth) || 0;
      const minHeight =
        lineHeight * (rows || 1) +
        paddingTop +
        paddingBottom +
        borderTop +
        borderBottom;
      el.style.setProperty('min-height', `${minHeight}px`, 'important');

      // Reset height first so scrollHeight reflects the full content height.
      el.style.setProperty('height', '0px', 'important');
      const newHeight = Math.max(el.scrollHeight, minHeight);
      const nextHeight = Math.ceil(newHeight);
      if (lastAppliedHeightRef.current !== nextHeight) {
        el.style.setProperty('height', `${nextHeight}px`, 'important');
        lastAppliedHeightRef.current = nextHeight;
      } else {
        el.style.setProperty('height', `${nextHeight}px`, 'important');
      }
    },
    [autoResize, rows],
  );

  // Adjust on mount and whenever value changes
  React.useLayoutEffect(() => {
    adjustHeight();

    if (autoResize) {
      requestAnimationFrame(() => {
        adjustHeight();
      });
      const timeoutId = window.setTimeout(() => adjustHeight(), 60);
      return () => window.clearTimeout(timeoutId);
    }
  }, [value, rows, adjustHeight, autoResize]);

  React.useEffect(() => {
    if (!autoResize) return;
    const el = textareaRef.current;
    if (!el) return;
    if (typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => {
      const width = el.offsetWidth;
      if (lastObservedWidthRef.current === width) return;
      lastObservedWidthRef.current = width;
      requestAnimationFrame(() => {
        adjustHeight();
      });
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, [autoResize, adjustHeight]);

  // Listen to Collapse enter events to recompute height when becoming visible
  React.useEffect(() => {
    if (!autoResize) return;
    const handler = () =>
      requestAnimationFrame(() => {
        adjustHeight();
      });
    window.addEventListener('caremappr:collapse-entered', handler);
    return () =>
      window.removeEventListener('caremappr:collapse-entered', handler);
  }, [autoResize, adjustHeight]);

  return (
    <Form.Group controlId={controlId} className={className}>
      {label && (
        <Form.Label className={`ellipsis ${labelClassName}`}>
          {label}
        </Form.Label>
      )}
      <InputGroup>
        <Form.Control
          as="textarea"
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            // Cast event to textarea change event to satisfy prop typing
            onChange(e as React.ChangeEvent<HTMLTextAreaElement>);
            if (autoResize) {
              adjustHeight(e.currentTarget as HTMLTextAreaElement);
              requestAnimationFrame(() =>
                adjustHeight(e.currentTarget as HTMLTextAreaElement),
              );
            }
          }}
          onInput={(e) => {
            if (autoResize) {
              adjustHeight(e.currentTarget as HTMLTextAreaElement);
            }
          }}
          rows={rows}
          required={required}
          disabled={disabled}
          className={inputClassName}
          ref={textareaRef}
          {...props}
        />
      </InputGroup>
    </Form.Group>
  );
};

export default CareMapprTextarea;
